import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import {
    Button,
    Card,
    CardContent,
    FormControl,
    FormLabel,
    Grid,
    Input,
    List,
    ListItem,
    Typography,
    IconButton,
    Autocomplete,
    Snackbar,
    Sheet,
    Switch,
} from "@mui/joy";
import { Save, Delete, Add, VisibilityOff, Visibility } from "@mui/icons-material";
import { CLayout } from "../../../components";
import { get, patch, post } from "../../../api/api";
import "../style.scss";
import {jwtDecode} from "jwt-decode";

interface StripeSubscription {
    id: string;
    status: string;
    start_date: number;
    current_period_start: number;
    current_period_end: number;
    plan: {
        id: string;
        nickname?: string;
        currency: string;
        interval: string;
        product: string;
    };
    items: {
        data: {
            price: {
                id: string;
                unit_amount: number;
                currency: string;
            };
        }[];
    };
    customer: string;
}

interface User {
    id: number;
    email: string;
    roles: string[];
    subscription_plans: string[];
    name: string;
    stripe_subscription?: StripeSubscription;
}

interface SubscriptionPlan {
    id: number;
    name: string;
    stripe_plan_id: string;
    product?: {
        description: string;
        price: string;
    };
}

export const VShowUser: React.FC = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();

    const [user, setUser] = useState<User | null>(null);
    const [id] = useState<string>(searchParams.get("id") || "0");
    const [source] = useState<string>(searchParams.get("source") || "admin");
    const [errors, setErrors] = useState<{ name?: string; email?: string }>({});
    const [decodedUser, setDecodedUser] = useState<any>({});
    const [visible, setVisible] = useState<boolean>(false);
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        color: "danger" | "primary" | "neutral" | "success" | "warning" | undefined;
        message: string;
    }>({
        open: false,
        color: "success",
        message: "",
    });
    const [subscriptionPlans, setSubscriptionPlans] = useState<SubscriptionPlan[]>([]);
    const [matchedPlan, setMatchedPlan] = useState<SubscriptionPlan | null>(null);

    // Détermine si l'utilisateur connecté est admin
    const isAdmin = decodedUser?.roles?.includes("ROLE_ADMIN");

    useEffect(() => {
        document.title = `EevenPro - ${t("Détail utilisateur")}`;
    }, [t]);

    useEffect(() => {
        const token = localStorage.getItem("token") || "";
        const decode: any = jwtDecode(token);
        // Si source = "admin", on vérifie que le JWT possède ROLE_ADMIN
        if (source === "admin" && !decode.roles.includes("ROLE_ADMIN")) {
            window.location.href = "/";
        }
        setDecodedUser(decode);
    }, [source]);

    useEffect(() => {
        // Récupération des données utilisateur
        void get({ url: `users/${id}` })
            .then((res) => setUser(res.data))
            .catch((err) => console.log(err));
    }, [id]);

    useEffect(() => {
        // Récupération des plans de souscription
        void get({ url: `subscription_plans` })
            .then((res) => setSubscriptionPlans(res.data))
            .catch((err) => console.log(err));
    }, []);

    useEffect(() => {
        // Dès que l'utilisateur et les plans sont chargés, on cherche à trouver le plan correspondant
        if (user && user.stripe_subscription && subscriptionPlans.length > 0) {
            const matched = subscriptionPlans.find(
                (plan) => plan.stripe_plan_id === user.stripe_subscription?.plan?.product
            );
            setMatchedPlan(matched || null);
        }
    }, [user, subscriptionPlans]);

    const handleInputChange = (key: string, value: string) => {
        setUser((prev: any) => ({
            ...prev,
            [key]: value,
        }));
    };

    const validateForm = () => {
        let newErrors: { name?: string; email?: string } = {};
        // Pour les utilisateurs non-admin, le nom n'est pas modifiable, donc pas de validation ici.
        if (source !== "user" && (!user?.name || user.name.trim() === "")) {
            newErrors.name = "Le nom est requis.";
        }
        if (!user?.email) {
            newErrors.email = "L'email est requis.";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Switch pour activer/désactiver ROLE_ADMIN (affiché uniquement pour source=admin)
    const toggleAdminRole = (checked: boolean) => {
        if (user) {
            let updatedRoles = [...user.roles];
            if (checked && !updatedRoles.includes("ROLE_ADMIN")) {
                updatedRoles.push("ROLE_ADMIN");
            } else if (!checked && updatedRoles.includes("ROLE_ADMIN")) {
                updatedRoles = updatedRoles.filter((r) => r !== "ROLE_ADMIN");
            }
            setUser({ ...user, roles: updatedRoles });
        }
    };

    const submit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!validateForm()) {
            return;
        }
        const formData = new FormData(event.currentTarget);
        const data = Object.fromEntries(formData.entries());
        if (data.plain_password !== data.retype_password) {
            setSnackbar({
                open: true,
                color: "danger",
                message: "Les mots de passe ne correspondent pas.",
            });
        } else {
            void patch({
                url: `users/${id}`,
                data: {
                    ...data,
                    roles: user?.roles,
                    subscription_plans: user?.subscription_plans,
                },
            })
                .then(() => {
                    setSnackbar({
                        open: true,
                        color: "success",
                        message: "Utilisateur modifié",
                    });
                })
                .catch((err) => console.log(err));
        }
    };

    // Fonction pour envoyer l'email de réinitialisation
    const handleForgotPassword = () => {
        if (user?.email) {
            void post({
                url: "forgot-password/",
                data: { email: user.email },
            })
                .then(() => {
                    setSnackbar({
                        open: true,
                        color: "success",
                        message: "Email de réinitialisation envoyé.",
                    });
                })
                .catch((err) => {
                    console.log(err);
                    setSnackbar({
                        open: true,
                        color: "danger",
                        message: "Erreur lors de l'envoi de l'email de réinitialisation.",
                    });
                });
        }
    };

    const formatTimestamp = (ts: number) =>
        new Date(ts * 1000).toLocaleDateString();

    return (
        <CLayout>
            <section id="VShowUser">
                <form onSubmit={submit}>
                    <Card className="cards" sx={{ p: 3, mb: 3, mt: 2 }}>
                        <Typography component="h1" level="h3" mb={2}>
                            {t("Détails de l'utilisateur")}
                        </Typography>
                        <Grid container spacing={2}>
                            {/* Informations de base */}
                            <Grid xs={12} md={6}>
                                {source === "admin" ? (
                                    <FormControl error={!!errors.name} sx={{ mb: 2 }}>
                                        <FormLabel>{t("Nom")}</FormLabel>
                                        <Input
                                            name="name"
                                            value={user?.name || ""}
                                            onChange={(e) => handleInputChange("name", e.target.value)}
                                        />
                                        {errors.name && (
                                            <Typography color="danger" level="body-sm">
                                                {errors.name}
                                            </Typography>
                                        )}
                                    </FormControl>
                                ) : (
                                    // Pour un utilisateur non-admin, le nom n'est pas modifiable
                                    <FormControl error={!!errors.name} sx={{ mb: 2 }}>

                                        <FormLabel>{t("Nom")}</FormLabel>
                                        <Input
                                            name="name"
                                            value={user?.name || ""}
                                            onChange={(e) => handleInputChange("name", e.target.value)}
                                            readOnly={true}
                                            className="!bg-gray-200"
                                        />
                                    </FormControl>

                                )}
                                <FormControl error={!!errors.email} sx={{ mb: 2 }}>
                                    <FormLabel>{t("Email")}</FormLabel>
                                    <Input
                                        name="email"
                                        value={user?.email || ""}
                                        onChange={(e) => handleInputChange("email", e.target.value)}
                                    />
                                    {errors.email && (
                                        <Typography color="danger" level="body-sm">
                                            {errors.email}
                                        </Typography>
                                    )}
                                </FormControl>
                            </Grid>

                            {/* Gestion du mot de passe (pour l'utilisateur non-admin) */}
                            {source !== "admin" && (
                                <Grid xs={12} md={6}>
                                    <FormControl sx={{ mb: 2 }}>
                                        <FormLabel>{t("Mot de passe")}</FormLabel>
                                        <Input
                                            name="plain_password"
                                            type={visible ? "text" : "password"}
                                            endDecorator={
                                                <IconButton onClick={() => setVisible(!visible)}>
                                                    {visible ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            }
                                        />
                                    </FormControl>
                                    <FormControl sx={{ mb: 2 }}>
                                        <FormLabel>{t("Validation du mot de passe")}</FormLabel>
                                        <Input
                                            name="retype_password"
                                            type={visible ? "text" : "password"}
                                            endDecorator={
                                                <IconButton onClick={() => setVisible(!visible)}>
                                                    {visible ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            }
                                        />
                                    </FormControl>
                                </Grid>
                            )}

                            {/* Bouton pour envoyer l'email de réinitialisation */}
                            {source === "admin" && (
                            <Grid container spacing={2} sx={{ mt: 2, justifyContent: "center" }}>
                                <Grid>
                                    <Button variant="outlined" color="neutral" onClick={handleForgotPassword}>
                                        {t("Envoyer email de réinitialisation")}
                                    </Button>
                                </Grid>
                            </Grid>
                            )}

                            {/* Section Rôles (affichée uniquement pour admin) */}
                            {source === "admin" && (
                                <Grid xs={12}>
                                    <FormLabel>{t("Role admin")}</FormLabel>
                                    <Switch
                                        checked={user?.roles.includes("ROLE_ADMIN") || false}
                                        onChange={(e) => toggleAdminRole(e.target.checked)}
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </Card>

                    {/* Section Détails Abonnement */}
                    {source === "admin" ? (
                        // Pour admin, afficher toutes les infos d'abonnement
                        user?.stripe_subscription && (
                            <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
                                <CardContent>
                                    <Typography component="h2" level="h4" mb={1}>
                                        {t("Informations d'abonnement")}
                                    </Typography>
                                    <Grid container>
                                        <Grid xs={12} md={6}>
                                            <Typography>
                                                <strong><u>{t("Statut")} :</u></strong>{" "}
                                                {user.stripe_subscription.status ?? 'Inconnu'}
                                            </Typography>
                                            <Typography className="!mt-1">
                                                <strong><u>{t("Début")} :</u></strong>{" "}
                                                {formatTimestamp(user.stripe_subscription.start_date)}
                                            </Typography>
                                            <Typography className="!mt-1">
                                                <strong><u>{t("Période en cours")} :</u></strong>{" "}
                                                {formatTimestamp(
                                                    user.stripe_subscription.current_period_start
                                                )}{" - "}
                                                {formatTimestamp(
                                                    user.stripe_subscription.current_period_end
                                                )}
                                            </Typography>
                                            <Typography className="!mt-1">
                                                <strong><u>{t("Plan")} :</u></strong>{" "}
                                                {user.stripe_subscription.plan?.nickname ||
                                                    user.stripe_subscription.plan?.product}
                                            </Typography>
                                            {user.stripe_subscription.items?.data[0]?.price && (
                                                <Typography className="!mt-1">
                                                    <strong><u>{t("Prix")} :</u></strong>{" "}
                                                    {(
                                                        user.stripe_subscription.items?.data[0].price.unit_amount /
                                                        100
                                                    ).toFixed(2)}{" "}
                                                    {user.stripe_subscription.items?.data[0].price.currency.toUpperCase()}
                                                </Typography>
                                            )}
                                        </Grid>
                                        <Grid xs={12} md={6}>
                                            {matchedPlan && (
                                                <Card
                                                    variant="soft"
                                                    sx={{
                                                        p: 1,
                                                        mt: 1,
                                                        backgroundColor: "neutral.softBg",
                                                    }}
                                                >
                                                    <Typography>
                                                        <strong><u>{t("Plan de souscription associé")}:</u></strong>{" "}
                                                        {matchedPlan.name}
                                                    </Typography>
                                                    {matchedPlan.product && (
                                                        <>
                                                            <Typography>
                                                                <u>Description :</u> {matchedPlan.product.description}
                                                            </Typography>
                                                            <Typography>
                                                                <u>Prix :</u> {matchedPlan.product.price} €
                                                            </Typography>
                                                        </>
                                                    )}
                                                </Card>
                                            )}
                                            <Button
                                                component="a"
                                                href={`https://dashboard.stripe.com/customers/${user.stripe_subscription.customer}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{ mt: 1 }}
                                            >
                                                {t("Voir la fiche Stripe")}
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        )
                    ) : (
                        // Pour un utilisateur non-admin, afficher uniquement la carte "Plan de souscription associé"
                        user?.stripe_subscription &&
                        matchedPlan && (
                            <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
                                <CardContent>
                                    <Typography component="h1" level="h3" mb={2}>
                                        {t("Votre plan de souscription")}
                                    </Typography>
                                    <Card
                                        variant="soft"
                                        sx={{ p: 1, backgroundColor: "neutral.softBg" }}
                                    >
                                        <Typography level="body-sm">
                                            <strong>{t("Plan")}:</strong> {matchedPlan.name}
                                        </Typography>
                                        {matchedPlan.product && (
                                            <>
                                                <Typography level="body-xs">
                                                    {matchedPlan.product.description}
                                                </Typography>
                                                <Typography level="body-xs">
                                                    {matchedPlan.product.price} €
                                                </Typography>
                                            </>
                                        )}
                                    </Card>
                                </CardContent>
                            </Card>
                        )
                    )}

                    <Grid container spacing={2}>
                        <Grid xs={12} sx={{ textAlign: "right" }}>
                            <Button type="submit" startDecorator={<Save />}>
                                {t("Enregistrer")}
                            </Button>
                        </Grid>
                    </Grid>
                </form>
            </section>

            <Snackbar
                open={snackbar.open}
                variant="solid"
                color={snackbar.color}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                {snackbar.message}
            </Snackbar>
        </CLayout>
    );
};